import React, { useState } from 'react';
import Card from 'components/Card';
import CardHeader from 'components/CardHeader';
import ModalMakeCardPrimary from 'screens/ModalMakeCardPrimary';
import { PrimaryContainer, Container, Header, Warning } from './styles';
import ShowInformation from 'components/ShowInformation';
import { useMutation } from '@apollo/client';
import { DELETE_CREDIT_CARD } from 'graphql/service';
import { debouncedErrorMessage } from 'utils/messageUtil';
import { retry } from 'utils/util';
import { message } from 'antd';

const CreditCards = ({
  paymentRecords,
  setPaymentRecords,
  balance
}) => {

  const [makeCardPrimaryIsOpen, setMakeCardPrimaryIsOpen] = useState(false)

  const [cardId, setCardId] = useState('')

  const [deleteCreditCard] = useMutation(DELETE_CREDIT_CARD, {
    onError: (error) => {
      if (!error.networkError) {
        let content = error.message
        if(error.message.includes('Validation')) {
            content = JSON.stringify(error.graphQLErrors[0]?.extensions?.exception)
        }
        debouncedErrorMessage(content)
      }
    }
  })

  const handleDeleteCard = async (paymentCardId) => {
    // Remove the deleted card from the local state
    setPaymentRecords((prevRecords) => 
      prevRecords.filter(record => record.paymentCardId !== paymentCardId)
    )
    try {
      const res = await retry(() => (
        deleteCreditCard({
          variables: {
            paymentCardId: paymentCardId
          }
        })
      ))
      if(!res || !res.data || !res.data.removePaymentCard) {
        throw new Error('Failed to delete credit card')
      } else {
        message.success('Delete credit card successfully')
      }
    } catch (error) {
      // Revert the local state if deletion failed
      setPaymentRecords((prevRecords) => [
        ...prevRecords,
        paymentRecords.find(record => record.paymentCardId === paymentCardId)
      ])
      if (!error.networkError) {
        let content = error.message;
        if (error.message.includes("Validation")) {
          content = JSON.stringify(
            error.graphQLErrors[0]?.extensions?.exception
          );
        }
        debouncedErrorMessage(content);
      }
    }
  }

  const primaryCard = paymentRecords.find((paymentCard) => paymentCard.isPrimary)

  const paymentCardList = paymentRecords.map((paymentCard, index) => {
    if(!paymentCard.isPrimary) {
      return (
        <Container key={index}>
          <Header>
            <div
              className="primaryButton"
              onClick={() => {
                setCardId(paymentCard.paymentCardId)
                setMakeCardPrimaryIsOpen(true)
              }}
            >
              Make this card my primary card
            </div>
            <div
              className="warningButton"
              onClick={() => handleDeleteCard(paymentCard.paymentCardId)}
            >
              Delete Card
            </div>
          </Header>
          <ShowInformation
            width={'40%'}
            title="NAME"
            description={paymentCard.holderName}
            additionalstyle={`          
              @media only screen and (max-width: 767px) {
                width: 100%;
              }
            `}
          />
          <ShowInformation
            width={'45%'}
            title="CREDIT CARD"
            description={paymentCard.transaction.creditCardNumber}
            additionalstyle={`
              background-repeat: no-repeat;
              background-position: 150px 90%;

              @media only screen and (max-width: 767px) {
                width: 70%;
              }
            `}
          />
          <ShowInformation
            width={'11%'}
            title="EXPIRY"
            description={paymentCard.transaction.cardExpirationDate}
            additionalstyle={`          
              @media only screen and (max-width: 767px) {
                width: 23%;
              }
            `}
          />
          <ShowInformation
            width={'4%'}
            title="CVV"
            description="***"
            additionalstyle={`          
              @media only screen and (max-width: 767px) {
                width: 7%;
              }
            `}
          />
          <ShowInformation
            width={'40%'}
            title="ADDRESS"
            description={
              paymentCard.streetAddress +
              (paymentCard.streetAddressLine2 ? ", " + paymentCard.streetAddressLine2 : "") +
              (paymentCard.city ? ", " + paymentCard.city : "") +
              (paymentCard.province ? ", " + paymentCard.province : "") +
              (paymentCard.postalCode ? ", " + paymentCard.postalCode : "") +
              (paymentCard.country ? ", " + paymentCard.country : "")
            }
          />
          <ShowInformation
            width={'45%'}
            title="CARD CAPTURE LOCATION"
            description={primaryCard?.cardCaptureLocation || 'N/A'}
          />
        </Container>
      )
    }
    return null; // Add this line to fix the eslint error
  })

  return (
    <Card>
      <CardHeader>
        <div className='type'>CREDIT CARDS</div>
      </CardHeader>
      {primaryCard? 
        (<PrimaryContainer>
          <Header>
            <div>
              <span className='status'>Status: </span>
              <span className='info'>Primary Card</span>
            </div>
          </Header>
          <ShowInformation
            width={'40%'}
            title="NAME"
            description={primaryCard.holderName}
            additionalstyle={`          
              @media only screen and (max-width: 767px) {
                width: 100%;
              }
            `}
          />
          <ShowInformation
            width={'45%'}
            title="CREDIT CARD"
            description={primaryCard.transaction.creditCardNumber}
            additionalstyle={`
              background-repeat: no-repeat;
              background-position: 150px 90%;

              @media only screen and (max-width: 767px) {
                width: 70%;
              }
            `}
          />
          <ShowInformation
            width={'11%'}
            title="EXPIRY"
            description={primaryCard.transaction.cardExpirationDate}
            additionalstyle={`          
              @media only screen and (max-width: 767px) {
                width: 23%;
              }
            `}
          />
          <ShowInformation
            width={'4%'}
            title="CVV"
            description="***"
            additionalstyle={`          
              @media only screen and (max-width: 767px) {
                width: 7%;
              }
            `}
          />
          <ShowInformation
            width={'40%'}
            title="ADDRESS"
            description={
              primaryCard.streetAddress +
              (primaryCard.streetAddressLine2 ? ", " + primaryCard.streetAddressLine2 : "") +
              (primaryCard.city ? ", " + primaryCard.city : "") +
              (primaryCard.province ? ", " + primaryCard.province : "") +
              (primaryCard.postalCode ? ", " + primaryCard.postalCode : "") +
              (primaryCard.country ? ", " + primaryCard.country : "")
            }
          />
          {/* FIXME: need to confirm with backend team about this field */}
          <ShowInformation
            width={'45%'}
            title="CARD CAPTURE LOCATION"
            description={primaryCard.cardCaptureLocation || 'N/A'}
          />
        </PrimaryContainer>)
        : (<Warning>No primary card. Please select a card to be your primary card.</Warning>)
      }
      <ModalMakeCardPrimary
        isOpen={makeCardPrimaryIsOpen}
        setIsOpen={setMakeCardPrimaryIsOpen}
        paymentCardId={cardId}
        amount={balance}
        originCardNumber={primaryCard?.transaction.creditCardNumber}
        newCardNumber={paymentRecords.find(paymentCard => paymentCard.paymentCardId === cardId)?.transaction.creditCardNumber}
        setPaymentRecords={setPaymentRecords}
      />
      {
        paymentRecords.length? paymentCardList: 
          <Warning>No card has been added.</Warning> 
      }
    </Card>
  )
}

export default CreditCards;